import React from 'react';
import { OrderDetailsPanel as DetailsPanel } from '@ajat/design-system/Checkout';
import { useCart } from '../../../../context/CartContext';

/**
 * Shared "Order Details" side panel for all brand checkout pages: the live cart (products, packages, totals,
 * discount, the chosen payment's fee) handed to the package's presentational `OrderDetailsPanel`. Sections and
 * zero rows are hidden when empty.
 *
 * @param {Object} props
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {string}   props.brandSlug    - Cart brand slug ('clsdk' | 'clsse' | 'clsfi' | 'abcse')
 * @param {string}   props.priceSuffix  - Price suffix (',-' | ':-' | ' €')
 * @param {string}   props.title        - Panel title (localized)
 * @param {Object}   props.labels       - { products, packages, discount, total }
 * @param {number}   [props.fee]        - Selected payment fee; row rendered only when > 0
 * @param {string}   [props.feeLabel]   - Fee row label (e.g. 'Fakturagebyr')
 * @param {string}   props.totalDisplay - Pre-formatted total (already includes fee)
 */
const OrderDetailsPanel = ({
  isOpen,
  onClose,
  brandSlug,
  priceSuffix,
  title,
  labels,
  fee = 0,
  feeLabel,
  totalDisplay,
}) => {
  const { products, packages, productsTotal, packagesTotal, discountAmount } = useCart(brandSlug);

  const fmt = (v) => `${Math.round(Number(v) || 0)}${priceSuffix}`;

  const rows = [
    ...(products.length > 0 ? [{ key: 'products', label: labels.products, value: fmt(productsTotal) }] : []),
    ...(packages.length > 0 ? [{ key: 'packages', label: labels.packages, value: fmt(packagesTotal) }] : []),
    ...(discountAmount > 0 ? [{ key: 'discount', label: labels.discount, value: `-${fmt(discountAmount)}`, variant: 'discount' }] : []),
    ...(fee > 0 ? [{ key: 'fee', label: feeLabel, value: fmt(fee) }] : []),
  ];

  return (
    <DetailsPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      sections={[
        { heading: labels.products, items: products },
        { heading: labels.packages, items: packages },
      ]}
      rows={rows}
      totalLabel={labels.total}
      totalDisplay={totalDisplay}
      formatPrice={fmt}
    />
  );
};

export default OrderDetailsPanel;
